import { Card } from './chunk-WNZ465SZ.js';
import { Separator } from './chunk-RH5HKCVJ.js';
import { Skeleton } from './chunk-7AXDPYRM.js';
import { Popover, PopoverTrigger, PopoverContent } from './chunk-ZGQFIZ33.js';
import { Input } from './chunk-VYHSTPFS.js';
import { buttonVariants, Button } from './chunk-UDB35RLY.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React9 from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown, Circle, ChevronRight, MoreHorizontal, ChevronLeft, CalendarIcon, Minus, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cva } from 'class-variance-authority';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Slot } from '@radix-ui/react-slot';
import { DayPicker } from 'react-day-picker';

var Progress = React9.forwardRef(function Progress2({ className, value, ...props }, ref) {
  const pct = Math.min(100, Math.max(0, value ?? 0));
  return /* @__PURE__ */ jsx(
    ProgressPrimitive.Root,
    {
      ref,
      className: cn("relative h-2 w-full overflow-hidden rounded-full bg-secondary", className),
      ...props,
      children: /* @__PURE__ */ jsx(
        ProgressPrimitive.Indicator,
        {
          className: "h-full w-full flex-1 bg-brand transition-transform",
          style: { transform: `translateX(-${100 - pct}%)` }
        }
      )
    }
  );
});
var Switch = React9.forwardRef(function Switch2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SwitchPrimitive.Root,
    {
      ref,
      className: cn(
        "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-brand data-[state=unchecked]:bg-input",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        SwitchPrimitive.Thumb,
        {
          className: cn(
            "pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
          )
        }
      )
    }
  );
});
var Tabs = TabsPrimitive.Root;
var TabsList = React9.forwardRef(function TabsList2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    TabsPrimitive.List,
    {
      ref,
      className: cn(
        "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
        className
      ),
      ...props
    }
  );
});
var TabsTrigger = React9.forwardRef(function TabsTrigger2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    TabsPrimitive.Trigger,
    {
      ref,
      className: cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        className
      ),
      ...props
    }
  );
});
var TabsContent = React9.forwardRef(function TabsContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    TabsPrimitive.Content,
    {
      ref,
      className: cn("mt-2 ring-offset-background focus-visible:outline-none", className),
      ...props
    }
  );
});
var Accordion = AccordionPrimitive.Root;
var AccordionItem = React9.forwardRef(function AccordionItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AccordionPrimitive.Item, { ref, className: cn("border-b", className), ...props });
});
var AccordionTrigger = React9.forwardRef(function AccordionTrigger2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
    AccordionPrimitive.Trigger,
    {
      ref,
      className: cn(
        "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(ChevronDown, { className: "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" })
      ]
    }
  ) });
});
var AccordionContent = React9.forwardRef(function AccordionContent2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AccordionPrimitive.Content,
    {
      ref,
      className: "overflow-hidden text-sm data-[state=closed]:animate-out data-[state=open]:animate-in",
      ...props,
      children: /* @__PURE__ */ jsx("div", { className: cn("pb-4 pt-0", className), children })
    }
  );
});
var SIZES = {
  sm: "h-6 w-6 text-[10px]",
  md: "h-8 w-8 text-xs",
  lg: "h-10 w-10 text-sm",
  xl: "h-14 w-14 text-base"
};
function Avatar({ src, alt, fallback, size = "md", className }) {
  const [errored, setErrored] = React9.useState(false);
  const showImage = src && !errored;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-semibold text-muted-foreground",
        SIZES[size],
        className
      ),
      children: showImage ? /* @__PURE__ */ jsx(
        "img",
        {
          src,
          alt: alt ?? "",
          className: "h-full w-full object-cover",
          onError: () => setErrored(true)
        }
      ) : fallback ?? "?"
    }
  );
}
var alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-current [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/40 bg-destructive/5 text-destructive",
        warning: "border-risk-medium/40 bg-risk-medium/5 text-risk-medium",
        success: "border-finality-finalized/40 bg-finality-finalized/5 text-finality-finalized",
        info: "border-brand/40 bg-brand/5 text-brand"
      }
    },
    defaultVariants: { variant: "default" }
  }
);
var Alert = React9.forwardRef(function Alert2({ className, variant, ...props }, ref) {
  return /* @__PURE__ */ jsx("div", { ref, role: "alert", className: cn(alertVariants({ variant }), className), ...props });
});
var AlertTitle = React9.forwardRef(
  function AlertTitle2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("h5", { ref, className: cn("mb-1 font-medium leading-none tracking-tight", className), ...props });
  }
);
var AlertDescription = React9.forwardRef(
  function AlertDescription2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("div", { ref, className: cn("text-sm [&_p]:leading-relaxed", className), ...props });
  }
);
var RadioGroup = React9.forwardRef(function RadioGroup2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(RadioGroupPrimitive.Root, { ref, className: cn("grid gap-2", className), ...props });
});
var RadioGroupItem = React9.forwardRef(function RadioGroupItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "aspect-square h-4 w-4 rounded-full border border-brand text-brand ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx(Circle, { className: "h-2.5 w-2.5 fill-current text-current" }) })
    }
  );
});
var ScrollBar = React9.forwardRef(function ScrollBar2({ className, orientation = "vertical", ...props }, ref) {
  return /* @__PURE__ */ jsx(
    ScrollAreaPrimitive.ScrollAreaScrollbar,
    {
      ref,
      orientation,
      className: cn(
        "flex touch-none select-none transition-colors",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
    }
  );
});
var ScrollArea = React9.forwardRef(function ScrollArea2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(ScrollAreaPrimitive.Root, { ref, className: cn("relative overflow-hidden", className), ...props, children: [
    /* @__PURE__ */ jsx(ScrollAreaPrimitive.Viewport, { className: "h-full w-full rounded-[inherit]", children }),
    /* @__PURE__ */ jsx(ScrollBar, {}),
    /* @__PURE__ */ jsx(ScrollAreaPrimitive.Corner, {})
  ] });
});
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
var AlertDialogPortal = AlertDialogPrimitive.Portal;
var AlertDialogOverlay = React9.forwardRef(function AlertDialogOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Overlay,
    {
      ref,
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
});
var AlertDialogContent = React9.forwardRef(function AlertDialogContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsxs(AlertDialogPortal, { children: [
    /* @__PURE__ */ jsx(AlertDialogOverlay, {}),
    /* @__PURE__ */ jsx(
      AlertDialogPrimitive.Content,
      {
        ref,
        className: cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out sm:rounded-lg",
          className
        ),
        ...props
      }
    )
  ] });
});
function AlertDialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col space-y-2 text-center sm:text-left", className), ...props });
}
function AlertDialogFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className), ...props });
}
var AlertDialogTitle = React9.forwardRef(function AlertDialogTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Title, { ref, className: cn("text-lg font-semibold", className), ...props });
});
var AlertDialogDescription = React9.forwardRef(function AlertDialogDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Description, { ref, className: cn("text-sm text-muted-foreground", className), ...props });
});
var AlertDialogAction = React9.forwardRef(function AlertDialogAction2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Action, { ref, className: cn(buttonVariants(), className), ...props });
});
var AlertDialogCancel = React9.forwardRef(function AlertDialogCancel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Cancel,
    {
      ref,
      className: cn(buttonVariants({ variant: "outline" }), "mt-2 sm:mt-0", className),
      ...props
    }
  );
});
var Breadcrumb = React9.forwardRef(function Breadcrumb2({ ...props }, ref) {
  return /* @__PURE__ */ jsx("nav", { ref, "aria-label": "breadcrumb", ...props });
});
var BreadcrumbList = React9.forwardRef(
  function BreadcrumbList2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx(
      "ol",
      {
        ref,
        className: cn(
          "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
          className
        ),
        ...props
      }
    );
  }
);
var BreadcrumbItem = React9.forwardRef(
  function BreadcrumbItem2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("li", { ref, className: cn("inline-flex items-center gap-1.5", className), ...props });
  }
);
var BreadcrumbLink = React9.forwardRef(function BreadcrumbLink2({ asChild, className, ...props }, ref) {
  const Comp = asChild ? Slot : "a";
  return /* @__PURE__ */ jsx(Comp, { ref, className: cn("transition-colors hover:text-foreground", className), ...props });
});
var BreadcrumbPage = React9.forwardRef(
  function BreadcrumbPage2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx(
      "span",
      {
        ref,
        role: "link",
        "aria-disabled": "true",
        "aria-current": "page",
        className: cn("font-normal text-foreground", className),
        ...props
      }
    );
  }
);
function BreadcrumbSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx("li", { role: "presentation", "aria-hidden": "true", className: cn("[&>svg]:h-3.5 [&>svg]:w-3.5", className), ...props, children: children ?? /* @__PURE__ */ jsx(ChevronRight, {}) });
}
function BreadcrumbEllipsis({ className, ...props }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      role: "presentation",
      "aria-hidden": "true",
      className: cn("flex h-9 w-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" }),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "More" })
      ]
    }
  );
}
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DayPicker,
    {
      showOutsideDays,
      className: cn("p-3", className),
      classNames: {
        months: "flex flex-col sm:flex-row gap-4 relative",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center h-7",
        caption_label: "text-sm font-medium",
        nav: "flex items-center gap-1 absolute inset-x-1 top-0 justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        range_end: "day-range-end",
        selected: "bg-brand text-brand-foreground hover:bg-brand hover:text-brand-foreground focus:bg-brand focus:text-brand-foreground",
        today: "bg-accent text-accent-foreground",
        outside: "day-outside text-muted-foreground opacity-50",
        disabled: "text-muted-foreground opacity-50",
        range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames
      },
      components: {
        Chevron: ({ orientation, className: chevronClassName, ...chevronProps }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return /* @__PURE__ */ jsx(Icon, { className: cn("h-4 w-4", chevronClassName), ...chevronProps });
        }
      },
      ...props
    }
  );
}
var DEFAULT_FORMAT = new Intl.DateTimeFormat(void 0, {
  year: "numeric",
  month: "short",
  day: "numeric"
});
function DatePicker({
  value,
  defaultValue,
  onChange,
  placeholder = "Pick a date",
  disabled,
  className,
  formatLabel
}) {
  const [open, setOpen] = React9.useState(false);
  const [internal, setInternal] = React9.useState(defaultValue);
  const isControlled = value !== void 0 || onChange !== void 0;
  const selected = isControlled ? value : internal;
  const handleSelect = (date) => {
    if (!isControlled) setInternal(date);
    onChange?.(date);
    if (date) setOpen(false);
  };
  const label = selected ? (formatLabel ?? ((d) => DEFAULT_FORMAT.format(d)))(selected) : placeholder;
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "outline",
        className: cn("w-full justify-start text-left font-normal", !selected && "text-muted-foreground", className),
        children: [
          /* @__PURE__ */ jsx(CalendarIcon, { className: "mr-2 h-4 w-4" }),
          label
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "w-auto p-0", align: "start", children: /* @__PURE__ */ jsx(Calendar, { mode: "single", selected, onSelect: handleSelect, disabled, autoFocus: true }) })
  ] });
}
var DATE_FMT = new Intl.DateTimeFormat(void 0, { month: "short", day: "numeric" });
var DATETIME_FMT = new Intl.DateTimeFormat(void 0, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit"
});
function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}
function endOfDay(date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}
function isWholeDayRange(from, to) {
  const startsAtMidnight = !from || from.getTime() === startOfDay(from).getTime();
  const endsAtMidnight = !to || to.getTime() === endOfDay(to).getTime();
  return startsAtMidnight && endsAtMidnight;
}
function timeInputValue(date) {
  if (!date) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}
function withTimeOfDay(date, value, edge) {
  const parts = value.split(":");
  const asMinute = (raw, max) => {
    const parsed = Number.parseInt(raw ?? "", 10);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(Math.max(parsed, 0), max);
  };
  const next = new Date(date);
  next.setHours(
    asMinute(parts[0], 23),
    asMinute(parts[1], 59),
    edge === "end" ? 59 : 0,
    edge === "end" ? 999 : 0
  );
  return next;
}
function absoluteRangeFrom(picked, options = {}) {
  const { showTime = false, keepTimesFrom } = options;
  const carry = showTime ? keepTimesFrom : void 0;
  return {
    from: picked?.from ? carry?.from ? withTimeOfDay(picked.from, timeInputValue(carry.from), "start") : startOfDay(picked.from) : void 0,
    to: picked?.to ? carry?.to ? withTimeOfDay(picked.to, timeInputValue(carry.to), "end") : endOfDay(picked.to) : void 0
  };
}
var defaultRangeLabel = (from, to) => isWholeDayRange(from, to) ? `${DATE_FMT.format(from)} \u2013 ${DATE_FMT.format(to)}` : `${DATETIME_FMT.format(from)} \u2013 ${DATETIME_FMT.format(to)}`;
function DateRangePicker({
  value,
  defaultValue,
  onChange,
  presets,
  placeholder = "Select range",
  align = "start",
  numberOfMonths = 2,
  showTime = false,
  disabled,
  className,
  formatRange = defaultRangeLabel
}) {
  const [open, setOpen] = React9.useState(false);
  const [internal, setInternal] = React9.useState(defaultValue ?? {});
  const isControlled = value !== void 0 || onChange !== void 0;
  const current = isControlled ? value ?? {} : internal;
  const commit = (next) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };
  const selectPreset = (presetId) => {
    commit({ presetId });
    setOpen(false);
  };
  const selectRange = (range) => {
    const next = absoluteRangeFrom(range, { showTime, keepTimesFrom: current });
    commit(next);
    if (next.from && next.to && !showTime) setOpen(false);
  };
  const setBoundTime = (edge, time) => {
    if (!time) return;
    const anchor = edge === "start" ? current.from : current.to;
    if (!anchor) return;
    const stamped = withTimeOfDay(anchor, time, edge);
    commit(edge === "start" ? { ...current, from: stamped } : { ...current, to: stamped });
  };
  const activePreset = presets?.find((p) => p.id === current.presetId);
  const label = activePreset ? activePreset.label : current.from && current.to ? formatRange(current.from, current.to) : current.from ? DATE_FMT.format(current.from) : placeholder;
  const calendarSelected = current.from || current.to ? { from: current.from, to: current.to } : void 0;
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "outline",
        className: cn(
          "justify-start text-left font-normal",
          !activePreset && !current.from && "text-muted-foreground",
          className
        ),
        children: [
          /* @__PURE__ */ jsx(CalendarIcon, { className: "mr-2 h-4 w-4" }),
          label
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs(PopoverContent, { className: "flex w-auto p-0", align, children: [
      presets && presets.length > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1 p-2", role: "listbox", "aria-label": "Quick ranges", children: presets.map((preset) => /* @__PURE__ */ jsx(
          Button,
          {
            variant: preset.id === current.presetId ? "secondary" : "ghost",
            size: "sm",
            className: "justify-start font-normal",
            role: "option",
            "aria-selected": preset.id === current.presetId,
            onClick: () => selectPreset(preset.id),
            children: preset.label
          },
          preset.id
        )) }),
        /* @__PURE__ */ jsx(Separator, { orientation: "vertical", className: "h-auto" })
      ] }) : null,
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx(
          Calendar,
          {
            mode: "range",
            selected: calendarSelected,
            onSelect: selectRange,
            numberOfMonths,
            disabled,
            autoFocus: true
          }
        ),
        showTime ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Separator, {}),
          /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-3 p-3", children: [
            /* @__PURE__ */ jsx(
              TimeField,
              {
                label: "From",
                value: timeInputValue(current.from),
                onChange: (time) => setBoundTime("start", time),
                disabled: !current.from
              }
            ),
            /* @__PURE__ */ jsx(
              TimeField,
              {
                label: "To",
                value: timeInputValue(current.to),
                onChange: (time) => setBoundTime("end", time),
                disabled: !current.to
              }
            )
          ] })
        ] }) : null
      ] })
    ] })
  ] });
}
function TimeField({
  label,
  value,
  onChange,
  disabled
}) {
  return /* @__PURE__ */ jsxs("label", { className: "flex flex-1 flex-col gap-1 text-xs text-muted-foreground", children: [
    label,
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "time",
        value,
        disabled,
        onChange: (event) => onChange(event.target.value),
        className: cn(
          "h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )
      }
    )
  ] });
}
var CURATED_EMOJIS = [
  { emoji: "\u{1F680}", keywords: "rocket launch ship startup" },
  { emoji: "\u2B50", keywords: "star favorite" },
  { emoji: "\u{1F525}", keywords: "fire hot trending" },
  { emoji: "\u{1F4A1}", keywords: "idea bulb light" },
  { emoji: "\u{1F6E1}\uFE0F", keywords: "shield security protect" },
  { emoji: "\u{1F512}", keywords: "lock secure private" },
  { emoji: "\u{1F511}", keywords: "key access" },
  { emoji: "\u{1F3E2}", keywords: "office building company organization" },
  { emoji: "\u{1F3E6}", keywords: "bank finance" },
  { emoji: "\u{1F4BC}", keywords: "briefcase work business" },
  { emoji: "\u{1F4CA}", keywords: "chart analytics report bar" },
  { emoji: "\u{1F4C8}", keywords: "growth chart up trending" },
  { emoji: "\u{1F310}", keywords: "globe web world network" },
  { emoji: "\u2699\uFE0F", keywords: "gear settings config" },
  { emoji: "\u{1F9E9}", keywords: "puzzle integration piece" },
  { emoji: "\u{1F517}", keywords: "link chain connect" },
  { emoji: "\u26D3\uFE0F", keywords: "chain blockchain links" },
  { emoji: "\u{1F48E}", keywords: "diamond gem crypto" },
  { emoji: "\u{1FA99}", keywords: "coin token money" },
  { emoji: "\u{1F4B0}", keywords: "money bag finance" },
  { emoji: "\u{1F91D}", keywords: "handshake deal partner team" },
  { emoji: "\u{1F465}", keywords: "people team group users" },
  { emoji: "\u{1F44B}", keywords: "wave hello welcome" },
  { emoji: "\u{1F3AF}", keywords: "target goal aim" },
  { emoji: "\u{1F3C6}", keywords: "trophy win award" },
  { emoji: "\u2705", keywords: "check done complete success" },
  { emoji: "\u{1F331}", keywords: "seedling grow new sprout" },
  { emoji: "\u{1F30D}", keywords: "earth globe region world" },
  { emoji: "\u{1F9E0}", keywords: "brain smart ai intelligence" },
  { emoji: "\u{1F916}", keywords: "robot bot automation ai" },
  { emoji: "\u{1F4E6}", keywords: "package box product ship" },
  { emoji: "\u{1F6E0}\uFE0F", keywords: "tools build dev" },
  { emoji: "\u26A1", keywords: "bolt fast speed power" },
  { emoji: "\u{1F50D}", keywords: "search magnify investigate" },
  { emoji: "\u{1F4C1}", keywords: "folder files project" },
  { emoji: "\u{1F3F7}\uFE0F", keywords: "tag label" },
  { emoji: "\u{1F3A8}", keywords: "palette design art brand" },
  { emoji: "\u{1F31F}", keywords: "glowing star sparkle premium" },
  { emoji: "\u2764\uFE0F", keywords: "heart love" },
  { emoji: "\u{1F600}", keywords: "smile happy face" }
];
function EmojiPicker({
  value,
  onSelect,
  placeholder = "\u{1F642}",
  ariaLabel = "Pick an emoji",
  emojis = CURATED_EMOJIS,
  disabled,
  className
}) {
  const [open, setOpen] = React9.useState(false);
  const [query, setQuery] = React9.useState("");
  const filtered = React9.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return emojis;
    return emojis.filter((e) => e.emoji === q || e.keywords.includes(q));
  }, [emojis, query]);
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "icon",
        disabled,
        "aria-label": ariaLabel,
        className: cn("text-xl", className),
        children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: value || placeholder })
      }
    ) }),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-64 p-2", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          autoFocus: true,
          value: query,
          onChange: (e) => setQuery(e.target.value),
          placeholder: "Search emoji\u2026",
          className: "mb-2 h-8",
          "aria-label": "Search emoji"
        }
      ),
      /* @__PURE__ */ jsx(ScrollArea, { className: "h-44", children: filtered.length === 0 ? /* @__PURE__ */ jsx("p", { className: "px-1 py-6 text-center text-sm text-muted-foreground", children: "No matches" }) : /* @__PURE__ */ jsx("div", { className: "grid grid-cols-6 gap-1", children: filtered.map((entry) => /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => {
            onSelect(entry.emoji);
            setOpen(false);
            setQuery("");
          },
          className: cn(
            "flex h-9 w-9 items-center justify-center rounded-md text-xl transition-colors hover:bg-accent",
            value === entry.emoji && "bg-accent ring-2 ring-ring"
          ),
          "aria-label": entry.keywords.split(" ")[0],
          children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: entry.emoji })
        },
        entry.emoji
      )) }) })
    ] })
  ] });
}
var CLAMP_CLASS = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6"
};
function ExpandableText({
  text,
  lines = 2,
  monospace = false,
  toggleable = true,
  onExpandToFull,
  expandLabel = "Show more",
  collapseLabel = "Show less",
  openLabel = "Open",
  className
}) {
  const [expanded, setExpanded] = React9.useState(false);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-1", className), children: [
    /* @__PURE__ */ jsx(
      "p",
      {
        title: text,
        className: cn(
          "whitespace-pre-wrap break-words text-sm",
          monospace && "font-mono text-xs",
          !expanded && CLAMP_CLASS[lines]
        ),
        children: text
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      toggleable && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => setExpanded((v) => !v),
          className: "text-xs font-medium text-primary hover:underline",
          children: expanded ? collapseLabel : expandLabel
        }
      ),
      onExpandToFull && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => onExpandToFull(text),
          className: "text-xs font-medium text-muted-foreground hover:text-foreground hover:underline",
          children: openLabel
        }
      )
    ] })
  ] });
}
var TREND_ICON = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: Minus
};
var TREND_COLOR = {
  up: "text-finality-finalized",
  down: "text-risk-high",
  flat: "text-muted-foreground"
};
var PAD = {
  sm: "p-3",
  md: "p-4"
};
function DataCard({
  label,
  value,
  secondary,
  icon,
  trailing,
  mono,
  trend,
  delta,
  loading,
  onClick,
  size = "md",
  className
}) {
  const interactive = Boolean(onClick);
  const TrendIcon = trend ? TREND_ICON[trend] : null;
  const body = loading ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-16" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-5 w-24" })
  ] }) : /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
    icon && /* @__PURE__ */ jsx("div", { className: "mt-0.5 shrink-0 text-muted-foreground", children: icon }),
    /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
      /* @__PURE__ */ jsx("div", { className: "truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground", children: label }),
      /* @__PURE__ */ jsxs("div", { className: "mt-0.5 flex items-baseline gap-1.5", children: [
        /* @__PURE__ */ jsx("span", { className: cn("truncate text-base font-semibold leading-tight", mono && "font-mono tabular-nums"), children: value }),
        trend && TrendIcon && /* @__PURE__ */ jsxs("span", { className: cn("inline-flex items-center gap-0.5 text-xs font-medium", TREND_COLOR[trend]), children: [
          /* @__PURE__ */ jsx(TrendIcon, { className: "h-3 w-3" }),
          delta
        ] })
      ] }),
      secondary != null && /* @__PURE__ */ jsx("div", { className: cn("mt-0.5 truncate text-xs text-muted-foreground", mono && "font-mono"), children: secondary })
    ] }),
    trailing && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: trailing })
  ] });
  return /* @__PURE__ */ jsx(
    Card,
    {
      className: cn(
        PAD[size],
        interactive && "cursor-pointer transition-colors hover:border-ring hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className
      ),
      ...interactive ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        }
      } : {},
      children: body
    }
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AlertTitle, Avatar, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, CURATED_EMOJIS, Calendar, DataCard, DatePicker, DateRangePicker, EmojiPicker, ExpandableText, Progress, RadioGroup, RadioGroupItem, ScrollArea, ScrollBar, Switch, Tabs, TabsContent, TabsList, TabsTrigger, absoluteRangeFrom, alertVariants, isWholeDayRange, withTimeOfDay };
//# sourceMappingURL=chunk-CYYAZHY3.js.map
//# sourceMappingURL=chunk-CYYAZHY3.js.map